import { cn } from "@/lib/utils";
import { upsertScriptReplaceMark } from "@/utils/script-utils";
import { trimToWordCount } from "@/utils/text-word-util";
import type { ScriptReplaceMark } from "@/services/make-generate";
import type { KeyboardEventHandler, Ref } from "react";
import {
  forwardRef,
  TextareaHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScriptMarkedEditor } from "./script-marked-editor";

// 稳定常量，保持引用稳定，杜绝不必要的 DOM 重建而强抢焦点（必须！）
const EMPTY_RISK_WORDS: string[] = [];

interface ScriptCreationProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  value: string;
  tooltip: boolean;
  /** 是否开启口播划词修改读音（开启后使用可高亮的内容区，不再是纯 textarea） */
  enableWordSelection?: boolean;
  marks?: ScriptReplaceMark[];
  scriptPlaceholder?: string;
  className?: string;
  autoFocus?: boolean;
  maxLineHeight?: number;
  maxCount?: number;
  /** 敏感词检测 */
  openScriptIllegal?: boolean;
  riskWords?: string[];
  onChange: (text: string) => void;
  onMarksChange?: (marks: ScriptReplaceMark[]) => void;
}

/** 口播台词输入组件 */
export const ScriptCreation = forwardRef<HTMLTextAreaElement | HTMLDivElement, ScriptCreationProps>(({
  value,
  tooltip,
  scriptPlaceholder,
  openScriptIllegal = false,
  riskWords = [],
  onChange,
  className,
  autoFocus = true,
  maxLineHeight = 5,
  maxCount = 2400,
  enableWordSelection = false,
  marks = [],
  onMarksChange = () => { },
  onKeyDown,
  ...textareaProps
},
  ref
) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [placeholder, setPlaceholder] = useState("");
  const { t } = useTranslation();

  const mergedTextareaRef = (node: HTMLTextAreaElement | null) => {
    textareaRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const lineHeight = 20;
    const minHeight = lineHeight * 1;
    const maxHeight = lineHeight * maxLineHeight;

    textarea.style.height = "";
    if (textarea.scrollHeight > textarea.offsetHeight) {
      textarea.style.height = `${Math.max(
        minHeight,
        Math.min(textarea.scrollHeight, maxHeight)
      )}px`;
    } else {
      textarea.style.height = `${textarea.offsetHeight}px`;
    }
  };

  useEffect(() => {
    if (autoFocus && textareaRef.current && !enableWordSelection) {
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [autoFocus, enableWordSelection]);

  useEffect(() => {
    const fullText = scriptPlaceholder || t("script.placeholder");
    if (!fullText) return;

    setPlaceholder("");
    let currentIndex = 0;
    let intervalId: NodeJS.Timeout;

    const startTimer = setTimeout(() => {
      intervalId = setInterval(() => {
        setPlaceholder((prev) => {
          if (currentIndex < fullText.length) {
            const nextText = fullText.slice(0, currentIndex + 1);
            currentIndex++;
            return nextText;
          }
          clearInterval(intervalId);
          return prev;
        });
      }, 30);
    }, 300);

    return () => {
      clearTimeout(startTimer);
      clearInterval(intervalId);
    };
  }, [scriptPlaceholder, t]);

  useEffect(() => {
    if (!enableWordSelection) {
      adjustTextareaHeight();
    }
  }, [value, enableWordSelection]);

  if (enableWordSelection) {
    return (
      <div className="relative">
        <ScriptMarkedEditor
          ref={ref as Ref<HTMLDivElement>}
          value={value}
          marks={marks}
          riskWords={openScriptIllegal ? riskWords : EMPTY_RISK_WORDS}
          onChange={onChange}
          onMarksChange={onMarksChange}
          maxLineHeight={maxLineHeight}
          maxCount={maxCount}
          className={className}
          autoFocus={autoFocus}
          upsertMark={upsertScriptReplaceMark}
          onKeyDown={onKeyDown as unknown as KeyboardEventHandler<HTMLDivElement> | undefined}
          readOnly={textareaProps.readOnly}
        />
        {tooltip && (
          <div className="absolute -top-2 left-4 px-2 py-1 w-fit transform -translate-y-[100%] text-sm text-white font-medium bg-[rgba(117,66,247,1)] rounded-sm rounded-bl-none">
            {t("script.tooltip")}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <textarea
        ref={mergedTextareaRef}
        value={value}
        id="text"
        name="text"
        onChange={(e) => {
          onChange(trimToWordCount(e.target.value, maxCount));
          requestAnimationFrame(adjustTextareaHeight);
        }}
        placeholder={placeholder}
        minLength={100}
        maxLength={maxCount}
        className={cn(
          "overflow-y-auto w-full h-5 px-4 border-0 resize-none focus:outline-none text-sm text-[rgba(29,41,57,1)] placeholder:text-[#98A2B3] placeholder:select-none",
          className
        )}
        {...textareaProps}
        onKeyDown={onKeyDown}
      />
      {tooltip && (
        <div className="absolute -top-2 left-4 px-2 py-1 w-fit transform -translate-y-[100%] text-sm text-white font-medium bg-[rgba(117,66,247,1)] rounded-sm rounded-bl-none">
          {t("script.tooltip")}
        </div>
      )}
    </div>
  );
}
);

ScriptCreation.displayName = "ScriptCreation";
